/**
 * soliishjs Loader — hand-written IIFE entry point (~3KB minified)
 *
 * This file is built separately as an IIFE and served as the <script> tag target.
 * It synchronously sets window.soliishjs so consumer code can detect it immediately,
 * then lazy-loads the core ESM module on first API call.
 *
 * Built by: scripts/build-loader.js (via esbuild)
 * Output:   dist/soliish-loader.js  (also copied to dist/web.js for backward compat)
 */

interface SoliishCore {
  widget: (container: any, props: any) => void;
  cleanup: (container: any) => void;
  renderPreview: (container: any, previewConfig: any, widgetDomain?: string) => void;
  getAllWidgetComponentsConfig: () => any;
  getAllLayoutConfigs: () => any;
  getDefaultPropertiesConfig: () => any;
}

(function () {
  // Derive base URL from own <script> tag's src attribute
  function getBaseUrl(): string {
    const scripts = document.getElementsByTagName('script');
    for (let i = scripts.length - 1; i >= 0; i--) {
      const src = scripts[i].src;
      if (src && (src.includes('soliish-loader') || src.includes('web.js'))) {
        return src.substring(0, src.lastIndexOf('/') + 1);
      }
    }
    // Fallback: use document.currentScript (works during initial execution)
    const currentScript = document.currentScript as HTMLScriptElement | null;
    if (currentScript?.src) {
      return currentScript.src.substring(0, currentScript.src.lastIndexOf('/') + 1);
    }
    return '';
  }

  const baseUrl = getBaseUrl();
  let coreModule: SoliishCore | null = null;
  let coreLoadPromise: Promise<SoliishCore> | null = null;

  function loadCore(): Promise<SoliishCore> {
    if (coreModule) return Promise.resolve(coreModule);
    if (coreLoadPromise) return coreLoadPromise;

    coreLoadPromise = import(/* @vite-ignore */ baseUrl + 'web-core.js')
      .then((mod) => {
        coreModule = mod as SoliishCore;
        return coreModule;
      })
      .catch((err) => {
        console.error('[soliishjs] Failed to load core module:', err);
        coreLoadPromise = null; // Allow retry
        throw err;
      });

    return coreLoadPromise;
  }

  // Set window.soliishjs synchronously so polling-based consumers detect it immediately
  (window as any).soliishjs = {
    widget: async (container: any, props: any) => {
      const core = await loadCore();
      return core.widget(container, props);
    },

    cleanup: async (container: any) => {
      const core = await loadCore();
      return core.cleanup(container);
    },

    renderPreview: async (container: any, previewConfig: any, widgetDomain?: string) => {
      const core = await loadCore();
      return core.renderPreview(container, previewConfig, widgetDomain);
    },

    getAllWidgetComponentsConfig: async () => {
      const core = await loadCore();
      return core.getAllWidgetComponentsConfig();
    },

    getAllLayoutConfigs: async () => {
      const core = await loadCore();
      return core.getAllLayoutConfigs();
    },

    getDefaultPropertiesConfig: async () => {
      const core = await loadCore();
      return core.getDefaultPropertiesConfig();
    },

    /**
     * Prefetch screen chunks for predicted next screens.
     * App.tsx handles this internally via actual import() calls that cache
     * the loaded components. This method is kept as a no-op for backward
     * compat in case widget.pug or external code calls it.
     */
    prefetch: (_screenIds: string[]) => {
      // No-op: prefetching is handled by App.tsx's useEffect which uses
      // import() to load and cache screen modules in the background.
      // The widget.pug <link rel="prefetch"> tags provide an additional
      // browser-level cache hint but are not required.
    },
  };
})();
